<script setup lang="ts">
import { ALL_LOB } from '~/constants'

const { exclusionId, impact } = defineProps<{
  impact: ExclusionImpact
  exclusionId: string
  pending?: boolean
}>()

const workspace = useWorkspace()
const { activePreset } = useDatePresets()

const isAllLob = computed(() => workspace.connectorId === ALL_LOB)

const formatDollars = (cents = 0) =>
  formatCents(cents, NumeralFormats.DASH_DOLLARS)

const impactData = computed(() => {
  const params = {
    exclusion_ids: exclusionId,
    exclusion_state: 'specific_exclusions',
  }
  return {
    active: {
      label: 'Active',
      params: {
        ...params,
        insight_mode: 'active',
      },
      hasImpactedCount: Boolean(impact?.impacted_claim_count_active),
      impactedClaims: formatCount(impact?.impacted_claim_count_active),
      estimatedImpactMin: formatDollars(impact?.min_savings_opportunity_active),
      estimatedImpactMax: formatDollars(impact?.max_savings_opportunity_active),
    },
    observation: {
      label: 'Observation',
      params: {
        ...params,
        insight_mode: 'observation',
      },
      hasImpactedCount: Boolean(impact?.impacted_claims_observation),
      impactedClaims: formatCount(impact?.impacted_claims_observation),
      estimatedImpactMin: formatDollars(
        impact?.min_savings_opportunity_observation,
      ),
      estimatedImpactMax: formatDollars(
        impact?.max_savings_opportunity_observation,
      ),
    },
  }
})
</script>

<template>
  <section class="grid gap-4">
    <header>
      <div class="mb-2 flex items-center justify-between">
        <h2 class="h2 text-neutral-900">Analytics</h2>
      </div>
      <div class="flex space-x-1">
        <div
          class="flex h-full items-center justify-start space-x-1 rounded-full bg-neutral-50 px-3 py-1"
        >
          <p class="body-2 text-neutral-700">LOB</p>
          <p class="subtitle-2 text-neutral-900">
            <span
              class="max-w-prose overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {{ workspace.activeConnector?.name || '' }}
            </span>
          </p>
        </div>
        <div
          class="flex h-full items-center justify-start space-x-1 rounded-full bg-neutral-50 px-3 py-1"
          :class="{
            'min-w-30 animate-pulse bg-slate-200': pending,
          }"
        >
          <p class="body-2 text-neutral-700">Date range</p>
          <p class="subtitle-2 text-neutral-900">
            <span>
              {{ activePreset?.label }}
            </span>
          </p>
        </div>
      </div>
    </header>
    <div
      v-for="{
        estimatedImpactMax,
        estimatedImpactMin,
        hasImpactedCount,
        impactedClaims,
        label,
        params,
      } in impactData"
      class="rounded-lg border p-1 py-4"
    >
      <h3 class="body-1 mb-2 px-4 text-neutral-900">{{ label }}</h3>

      <div class="divide-x-1 grid w-full grid-cols-2 divide-neutral-500">
        <div class="px-4 py-1">
          <NuxtLink
            v-if="hasImpactedCount && !isAllLob"
            :to="{
              path: '/claims',
              query: params,
            }"
            class="h1 text-secondary-700 hover:underline"
            :data-testid="`${label.toLowerCase()}-impacted-claims-link`"
          >
            {{ impactedClaims }}
          </NuxtLink>
          <p v-else class="h1 text-neutral-900">{{ impactedClaims }}</p>
          <p class="body-2 text-neutral-500">Impacted claims</p>
        </div>
        <div class="px-4 py-1">
          <p class="h1 uppercase text-neutral-900">
            {{ estimatedImpactMin }} – {{ estimatedImpactMax }}
          </p>
          <p class="body-2 text-neutral-500">Estimated impact</p>
        </div>
      </div>
    </div>
  </section>
</template>
